import loadItemLabelBeforeCompose from '../utils/loadItemLabelBeforeCompose';
import { openComposeWithViewState, prepareComposeViewState } from './openCompose';
import trySaveAndCloseCompose from './trySaveAndCloseCompose';
import upgradeCompose from '../actions/upgradeCompose';
import datapoints from '../datapoints';
import {
    ComposeViewState,
    ComposeOperation,
    ComposeTarget,
    composeStore,
} from 'owa-mail-compose-store';
import { ComposeViewStateInitProps } from '../utils/createComposeViewState';
import createItemIdForRequest from '../utils/createItemIdForRequest';
import getBodyTypeForResponse from '../utils/getBodyTypeForResponse';
import getDocumentId from '../utils/getDocumentId';
import getCLPLabelExtendedPropertyValue from 'owa-mail-clp/lib/utils/getCLPLabelExtendedPropertyValue';
import getItemWithNormalizedBody from '../utils/getItemWithNormalizedBody';
import getQuotedBodyForSmimeItemReply from '../utils/getQuotedBodyForSmimeItemReply';
import getRespondSubject from '../utils/getRespondSubject';
import { infoBarAddHandler } from '../utils/infoBarHandlers';
import tryGetSmimeViewState from '../utils/tryGetSmimeViewState';
import { PerformanceDatapoint } from 'owa-analytics';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { isSmimeAttachment } from 'owa-attachment-model-store';
import { ClientItemId, MailboxType } from 'owa-client-ids';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ARCHIVE_FOLDERS_TREE_TYPE, SHARED_FOLDERS_TREE_TYPE } from 'owa-folders';
import { userMailInteractionAction } from 'owa-mail-actions/lib/triage/userMailInteractionAction';
import isCLPEnabled from 'owa-mail-clp/lib/utils/isCLPEnabled';
import getClassificationFromMessage from 'owa-mail-message-classification/lib/utils/getClassificationFromMessage';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { ClientItem, InstrumentationContext } from 'owa-mail-store';
import createItem from 'owa-mail-store/lib/services/createItem';
import createResponseFromModernGroupItem from 'owa-mail-store/lib/services/createResponseFromModernGroupItem';
import store from 'owa-mail-store/lib/store/Store';
import { isDeepLink } from 'owa-popout';
import BodyType from 'owa-service/lib/contract/BodyType';
import ForwardItem from 'owa-service/lib/contract/ForwardItem';
import Item from 'owa-service/lib/contract/Item';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import Message from 'owa-service/lib/contract/Message';
import forwardItem from 'owa-service/lib/factory/forwardItem';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { lazyGetAttachmentsForSmimeResponse } from 'owa-smime';
import SmimeErrorStateEnum from 'owa-smime-adapter/lib/store/schema/SmimeErrorStateEnum';
import showErrorForCloudOrUriAttachments from 'owa-smime/lib/utils/attachments/showErrorForCloudOrUriAttachments';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import isSMIMEItem from 'owa-smime/lib/utils/isSMIMEItem';
import { isSxSDisplayed, lazyLogUsageWithSxSCustomData } from 'owa-sxs-store';
import { trace } from 'owa-trace';
import { select } from 'satcheljs/lib/legacy';

export interface ForwardMessageState {
    referenceItem: Item;
    primaryComposeId: string;
}

export default createLegacyActionWithDatapoint('forwardMessage', datapoints.MailComposeForward)(
    select<ForwardMessageState>({
        referenceItem: (referenceClientItemId: ClientItemId) =>
            store.items.get(referenceClientItemId.Id),
        primaryComposeId: () => composeStore.primaryComposeId,
    })(async function forwardMessage(
        referenceClientItemId: ClientItemId,
        actionSource: ActionSource,
        instrumentationContexts: InstrumentationContext[],
        suppressServerMarkReadOnReplyOrForward: boolean,
        state?: ForwardMessageState
    ) {
        let viewState: ComposeViewState = null;
        const openInSxS = isSxSDisplayed();
        const openInTab = !isDeepLink();
        if (!openInSxS && !openInTab) {
            await trySaveAndCloseCompose(null /*viewState*/);

            // If there is still default compose viewstate, it means it is failed to save and close previous compose
            // So no-op here
            if (state.primaryComposeId) {
                return Promise.resolve();
            }
        }

        let referenceItem = state.referenceItem as Message;
        const bodyType = getBodyTypeForResponse(referenceItem);
        const composeOperation = ComposeOperation.Forward;
        const documentId = getDocumentId(referenceItem);
        const referenceItemId = createItemIdForRequest(referenceItem.ItemId);

        const item = forwardItem({
            ReferenceItemId: referenceItemId,
            ReferenceItemDocumentId: documentId,
            Subject: getRespondSubject(referenceItem, composeOperation),
        });

        const mailboxInfo = referenceClientItemId.mailboxInfo;
        const groupId =
            mailboxInfo && mailboxInfo.type == MailboxType.GroupMailbox
                ? mailboxInfo.deprecatedMailboxId
                : null;
        const openTarget = openInSxS
            ? ComposeTarget.SxS
            : openInTab
            ? ComposeTarget.SecondaryTab
            : ComposeTarget.PrimaryReadingPane;

        const smimeViewState = await tryGetSmimeViewState(
            composeOperation,
            referenceItem as ClientItem
        );

        let existingLabelString = '';
        if (isCLPEnabled()) {
            existingLabelString = getCLPLabelExtendedPropertyValue(referenceItem);
        }

        let classification = null;
        if (isFeatureEnabled('cmp-message-classification')) {
            classification = getClassificationFromMessage(referenceItem);
        }

        const commonComposeInitProps: ComposeViewStateInitProps = {
            operation: composeOperation,
            bodyType: bodyType,
            subject: item.Subject,
            referenceItemId: referenceItem.ItemId,
            referenceItemDocumentId: documentId,
            smimeViewState,
            existingLabelString: existingLabelString,
            classification,
            conversationId: referenceItem.ConversationId ? referenceItem.ConversationId.Id : null,
            isInlineCompose: false,
        };

        /**
         * Below check decides whether the forward mail will be an S/MIME mail
         * or not. This condition has to be first one in the below if..else block
         * because if S/MIME admin settings are enabled then by default every forward mail will
         * be an S/MIME mail. Also, if the message is an S/MIME mail or an attachment of an S/MIME mail, we need to
         * ensure that we treat it like a S/MIME mail and not make the server call since server has no info about this mail.
         */
        if (
            !groupId &&
            isFeatureEnabled('cmp-smime') &&
            (isSmimeAttachment(referenceItem.ItemId.Id) ||
                isSMIMEItem(referenceItem) ||
                isSmimeEnabledInViewState(smimeViewState))
        ) {
            // Start datapoint for monitoring reliability of forwards in S/MIME (VSO 58600)
            const smimeDatapoint = new PerformanceDatapoint('ForwardInSMIME');

            /**
             * Fetch Item from server in case normalized body is missing.
             */
            if (!referenceItem.NormalizedBody) {
                try {
                    referenceItem = await getItemWithNormalizedBody(referenceItem.ItemId.Id);
                } catch (error) {
                    trace.error(
                        `SMIME: The reference item's NormalizedBody cannot be retrieved while forwarding. ${error}`
                    );
                    smimeViewState.errorCode = SmimeErrorStateEnum.QuotedBodyIsNotComplete;
                }
            }

            try {
                const quotedBody = await getQuotedBodyForSmimeItemReply(
                    referenceItem,
                    composeOperation,
                    bodyType === 'HTML'
                );

                const {
                    supportedSmimeAttachments,
                    unsupportedSmimeAttachmentNames,
                } = (await lazyGetAttachmentsForSmimeResponse.import())(
                    referenceItem.Attachments,
                    referenceItem.ItemId,
                    true /* isForward */
                );

                const composeInitProps: ComposeViewStateInitProps = {
                    ...commonComposeInitProps,
                    preferAsyncSend: false,
                    useSmartResponse: false,
                    IRMData: isEncryptionEnabled()
                        ? referenceItem.RightsManagementLicenseData
                        : null,
                    quotedBody,
                    attachments: supportedSmimeAttachments,
                };

                viewState = await prepareComposeViewState(composeInitProps, null /* groupId */);
                showErrorForCloudOrUriAttachments(
                    unsupportedSmimeAttachmentNames,
                    infoBarAddHandler(viewState)
                );
                openComposeWithViewState(viewState, openTarget);

                smimeDatapoint.end();
            } catch (error) {
                smimeDatapoint.endWithError(error);
                throw error;
            }
        } else {
            const composeInitProps: ComposeViewStateInitProps = {
                ...commonComposeInitProps,
                preferAsyncSend: true,
                IRMData: isEncryptionEnabled() ? referenceItem.RightsManagementLicenseData : null,
                useSmartResponse: true, // Set useSmartResponse to true in case we can't get quoted body
            };

            viewState = await prepareComposeViewState(composeInitProps, groupId);
            openComposeWithViewState(viewState, openTarget);

            upgradeCompose(
                viewState,
                createForward(groupId, item, bodyType, suppressServerMarkReadOnReplyOrForward)
            );
        }

        if (viewState) {
            loadItemLabelBeforeCompose(existingLabelString, referenceItem, viewState.composeId);
        }

        userMailInteractionAction('Forward', instrumentationContexts);

        if (openInSxS) {
            lazyLogUsageWithSxSCustomData.importAndExecute('ForwardInSxS');
        }
    })
);

async function createForward(
    groupId: string,
    item: ForwardItem,
    bodyType: BodyType,
    suppressServerMarkReadOnReplyOrForward: boolean
): Promise<Message> {
    const timeFormat = getUserConfiguration().UserOptions.TimeFormat;
    const selectedNode = getSelectedNode();
    // To fix the XRF access issue, we set RemoteExecute for archive and shared folder scenarios.
    // For CreateItem, we do not set X-AnchorMailbox header, the request will always hit primary mailbox user's mailbox server.
    // On server side code, we have code fix to check the flag when reply/replyall/forward emails.
    const remoteExecute = !!(
        selectedNode &&
        (selectedNode.treeType === SHARED_FOLDERS_TREE_TYPE ||
            selectedNode.treeType === ARCHIVE_FOLDERS_TREE_TYPE)
    );
    try {
        const createForwardResponse = groupId
            ? await createResponseFromModernGroupItem(
                  groupId,
                  item,
                  bodyType,
                  false /*isSend*/,
                  timeFormat
              )
            : await createItem(
                  item,
                  bodyType,
                  false /*isSend*/,
                  timeFormat,
                  false /* isInlineCompose */,
                  null /* IRMData */,
                  null /*folderId*/,
                  remoteExecute,
                  suppressServerMarkReadOnReplyOrForward
              );

        const responseMessage = createForwardResponse.ResponseMessages
            .Items[0] as ItemInfoResponseMessage;
        return responseMessage.Items[0] as Message;
    } catch (error) {
        return null;
    }
}
